import { readBundledData } from "@faire/retailer-visitor-shared/lib/bundledData";
import { CACHE_HIT_KEY } from "@faire/retailer-visitor-shared/lib/cacheHitKey";
import { stubImage } from "@faire/retailer-visitor-shared/lib/preload-images/common";
import { useSettingDecentralizePageData } from "@faire/retailer-visitor-shared/settings/useSettingDecentralizePageData";
import { buildBannerImageSrcSet } from "@faire/retailer-visitor-shared/views/brand/BrandHeader/buildBannerImageSrcSet";
import { optimizeAvatar } from "@faire/web--source/slate/Avatar";
import getBrandView from "@faire/web-api--source/endpoints/www/api/v2/brand-view/brandToken/get";
import { getQueryKey as getQueryKeyBrandView } from "@faire/web-api--source/endpoints/www/api/v2/brand-view/brandToken/get-hooks";
import { IBrandViewV2Response } from "@faire/web-api--source/indigofair/data/IBrandViewV2Response";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

const BRAND_VIEW_STALE_TIME_MS = 5 * 60 * 1000; // 5 min

export const useFetchCachedBrandView = (
  brandToken: string,
  brandViewDataPromise?:
    | Promise<IBrandViewV2Response | undefined>
    | typeof CACHE_HIT_KEY
): UseQueryResult<IBrandViewV2Response> => {
  const shouldDecentralizePageData = useSettingDecentralizePageData();
  return useQuery({
    enabled: !!brandToken,
    ...getBrandViewUseQueryArgs(
      brandToken,
      brandViewDataPromise,
      shouldDecentralizePageData
    ),
  });
};

export function getBrandViewUseQueryArgs(
  brandToken: string,
  brandViewDataPromise?:
    | Promise<IBrandViewV2Response | undefined>
    | typeof CACHE_HIT_KEY,
  shouldDecentralizePageData: boolean = false
) {
  const prefetched = shouldDecentralizePageData
    ? undefined
    : readBundledData("brand_view_v2", { keep: true });
  return {
    initialData: () => {
      if (prefetched?.brand?.token === brandToken) {
        return prefetched;
      }
      return undefined;
    },
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getQueryKeyBrandView(brandToken),
    queryFn: () => {
      // TODO(matthew.bojey, 1 month from now): Figure out a better way to handle brand view caching, maybe do nothing because NextJS will handle it?
      if (brandViewDataPromise && brandViewDataPromise !== CACHE_HIT_KEY) {
        return brandViewDataPromise;
      }
      return getBrandView(brandToken);
    },
    refetchOnMount: false,
    staleTime: BRAND_VIEW_STALE_TIME_MS,
  };
}

export const usePreloadBuyItAgainAndImages = (brandToken: string) => {
  const { data } = useFetchCachedBrandView(brandToken ?? "");

  useEffect(() => {
    if (!brandToken) {
      return;
    }
    if (data?.brand) {
      preloadImagesBrandPageLoggedIn(data.brand);
    }
  }, [data, brandToken]);
};

export const preloadImagesBrandPageLoggedIn = (
  brand: IBrandViewV2Response.ILeanBrand
) => {
  const { profile_image, banner_image } = brand;
  const isSSR = false;

  if (profile_image) {
    const src = optimizeAvatar(profile_image, "x-large", isSSR);
    stubImage(src);
  }

  if (banner_image) {
    const { src, srcset, sizes } = buildBannerImageSrcSet(banner_image);
    stubImage(src, srcset, sizes);
  }
};
